import React, { useState } from 'react';
import { UserIcon, MailIcon, PhoneIcon, BikeIcon, ShieldCheckIcon, SendIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  bikeModel: string;
  engineSize: string;
  licence: string;
  raceClass: string;
}
interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  bikeModel?: string;
  engineSize?: string;
  licence?: string;
  raceClass?: string;
}
function validateForm(data: FormData): FormErrors {
  const errors: FormErrors = {};
  if (!data.firstName.trim()) errors.firstName = 'Vārds ir obligāts';
  if (!data.lastName.trim()) errors.lastName = 'Uzvārds ir obligāts';
  if (!data.email.trim()) {
    errors.email = 'E-pasts ir obligāts';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
    errors.email = 'Lūdzu, ievadiet derīgu e-pasta adresi';
  }
  if (!data.phone.trim()) {
    errors.phone = 'Telefona numurs ir obligāts';
  } else if (!/^[+]?[\d\s\-()]{7,}$/.test(data.phone)) {
    errors.phone = 'Lūdzu, ievadiet derīgu telefona numuru';
  }
  if (!data.bikeModel.trim()) errors.bikeModel = 'Motocikla modelis ir obligāts';
  if (!data.engineSize.trim()) {
    errors.engineSize = 'Dzinēja tilpums ir obligāts';
  } else if (isNaN(Number(data.engineSize)) || Number(data.engineSize) <= 0) {
    errors.engineSize = 'Lūdzu, ievadiet derīgu dzinēja tilpumu cm³';
  }
  if (!data.raceClass) errors.raceClass = 'Lūdzu, izvēlieties sacensību klasi';
  if (data.raceClass === 'pro' && !data.licence.trim()) {
    errors.licence = 'Sacensību licence ir obligāta Pro klasei';
  }
  return errors;
}
export function RegistrationForm() {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    bikeModel: '',
    engineSize: '',
    licence: '',
    raceClass: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  }
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    setErrors({});
    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus('success');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        bikeModel: '',
        engineSize: '',
        licence: '',
        raceClass: ''
      });
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  }
  if (submitStatus === 'success') {
    return <section id="register" className="w-full bg-neutral-950 bg-noise py-20 md:py-28 px-4" data-id="element-573">
        <div className="max-w-2xl mx-auto text-center" data-id="element-574">
          <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6" data-id="element-575">
            <CheckCircleIcon className="h-10 w-10 text-green-500" data-id="element-576" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl tracking-wider text-white mb-4" data-id="element-577">
            REĢISTRĀCIJA PABEIGTA
          </h2>
          <p className="text-gray-400 text-lg mb-8" data-id="element-578">
            Tu esi iekšā! Pārbaudi savu e-pastu, lai saņemtu apstiprinājuma
            informāciju un sacensību dienas instrukcijas. Tiekamies startā.
          </p>
          <button onClick={() => setSubmitStatus('idle')} className="px-8 py-3 bg-orange-500 hover:bg-orange-600 text-black font-bold uppercase tracking-wider rounded transition-colors" data-id="element-579">
            Reģistrēt vēl vienu braucēju
          </button>
        </div>
      </section>;
  }
  return <section id="register" className="w-full bg-neutral-950 bg-noise py-20 md:py-28 px-4" data-id="element-580">
      <div className="max-w-3xl mx-auto" data-id="element-581">
        {/* Section Header */}
        <div className="text-center mb-12" data-id="element-582">
          <p className="text-orange-500 font-semibold text-sm uppercase tracking-[0.3em] mb-3" data-id="element-583">
            Nodrošini savu vietu
          </p>
          <h2 className="font-display text-4xl md:text-6xl tracking-wider text-white mb-4" data-id="element-584">
            REĢISTRĒTIES TAGAD
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto" data-id="element-585">
            Aizpildi savus datus zemāk. Ierobežots vietu skaits — nepalaid
            garām.
          </p>
        </div>

        {/* Form */}
        <form action="https://api.web3forms.com/submit" method="POST" onSubmit={handleSubmit} noValidate className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 md:p-10" data-id="element-586">
          <input type="hidden" name="access_key" value="58247f00-ccb7-40b2-8571-dd9f583b0c38" data-id="element-587" />
          {submitStatus === 'error' && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3" data-id="element-588">
              <AlertCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0" data-id="element-589" />
              <p className="text-red-400 text-sm" data-id="element-590">
                Kaut kas nogāja greizi. Lūdzu, mēģiniet vēlreiz.
              </p>
            </div>}

          {/* Name Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5" data-id="element-591">
            <div data-id="element-592">
              <label htmlFor="firstName" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-593">
                <span className="flex items-center gap-2" data-id="element-594">
                  <UserIcon className="h-4 w-4 text-gray-500" data-id="element-595" />
                  Vārds *
                </span>
              </label>
              <input type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="Ievadiet savu vārdu" className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.firstName ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.firstName} aria-describedby={errors.firstName ? 'firstName-error' : undefined} data-id="element-596" />
              {errors.firstName && <p id="firstName-error" className="mt-1.5 text-xs text-red-400" data-id="element-597">
                  {errors.firstName}
                </p>}
            </div>
            <div data-id="element-598">
              <label htmlFor="lastName" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-599">
                <span className="flex items-center gap-2" data-id="element-600">
                  <UserIcon className="h-4 w-4 text-gray-500" data-id="element-601" />
                  Uzvārds *
                </span>
              </label>
              <input type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Ievadiet savu uzvārdu" className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.lastName ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.lastName} aria-describedby={errors.lastName ? 'lastName-error' : undefined} data-id="element-602" />
              {errors.lastName && <p id="lastName-error" className="mt-1.5 text-xs text-red-400" data-id="element-603">
                  {errors.lastName}
                </p>}
            </div>
          </div>

          {/* Email */}
          <div className="mb-5" data-id="element-604">
            <label htmlFor="email" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-605">
              <span className="flex items-center gap-2" data-id="element-606">
                <MailIcon className="h-4 w-4 text-gray-500" data-id="element-607" />
                E-pasta adrese *
              </span>
            </label>
            <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.email ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'email-error' : undefined} data-id="element-608" />
            {errors.email && <p id="email-error" className="mt-1.5 text-xs text-red-400" data-id="element-609">
                {errors.email}
              </p>}
          </div>

          {/* Phone */}
          <div className="mb-5" data-id="element-610">
            <label htmlFor="phone" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-611">
              <span className="flex items-center gap-2" data-id="element-612">
                <PhoneIcon className="h-4 w-4 text-gray-500" data-id="element-613" />
                Telefona numurs *
              </span>
            </label>
            <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="+371 20000000" className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.phone ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? 'phone-error' : undefined} data-id="element-614" />
            {errors.phone && <p id="phone-error" className="mt-1.5 text-xs text-red-400" data-id="element-615">
                {errors.phone}
              </p>}
          </div>

          {/* Bike Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5" data-id="element-616">
            <div data-id="element-617">
              <label htmlFor="bikeModel" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-618">
                <span className="flex items-center gap-2" data-id="element-619">
                  <BikeIcon className="h-4 w-4 text-gray-500" data-id="element-620" />
                  Motocikla modelis *
                </span>
              </label>
              <input type="text" id="bikeModel" name="bikeModel" value={formData.bikeModel} onChange={handleChange} placeholder="e.g. KTM 300 EXC" className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.bikeModel ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.bikeModel} aria-describedby={errors.bikeModel ? 'bikeModel-error' : undefined} data-id="element-621" />
              {errors.bikeModel && <p id="bikeModel-error" className="mt-1.5 text-xs text-red-400" data-id="element-622">
                  {errors.bikeModel}
                </p>}
            </div>
            <div data-id="element-623">
              <label htmlFor="engineSize" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-624">
                Dzinēja tilpums (cm³) *
              </label>
              <input type="text" id="engineSize" name="engineSize" value={formData.engineSize} onChange={handleChange} placeholder="e.g. 300" className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.engineSize ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.engineSize} aria-describedby={errors.engineSize ? 'engineSize-error' : undefined} data-id="element-625" />
              {errors.engineSize && <p id="engineSize-error" className="mt-1.5 text-xs text-red-400" data-id="element-626">
                  {errors.engineSize}
                </p>}
            </div>
          </div>

          {/* Race Class */}
          <div className="mb-5" data-id="element-627">
            <label htmlFor="raceClass" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-628">
              Sacensību klase *
            </label>
            <select id="raceClass" name="raceClass" value={formData.raceClass} onChange={handleChange} className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors appearance-none ${errors.raceClass ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'} ${!formData.raceClass ? 'text-gray-600' : ''}`} aria-invalid={!!errors.raceClass} aria-describedby={errors.raceClass ? 'raceClass-error' : undefined} data-id="element-629">
              <option value="" disabled data-id="element-630">
                Izvēlieties sacensību klasi
              </option>
              <option value="beginner" data-id="element-631">Iesācēji - €100 (10 km)</option>
              <option value="hobby" data-id="element-632">Hobby — €100 (12 km)</option>
              <option value="pro" data-id="element-633">Pro — €100 (13 km)</option>
            </select>
            {errors.raceClass && <p id="raceClass-error" className="mt-1.5 text-xs text-red-400" data-id="element-634">
                {errors.raceClass}
              </p>}
          </div>

          {/* Racing Licence */}
          <div className="mb-8" data-id="element-635">
            <label htmlFor="licence" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-636">
              <span className="flex items-center gap-2" data-id="element-637">
                <ShieldCheckIcon className="h-4 w-4 text-gray-500" data-id="element-638" />
                Sacensību licences numurs{' '}
                {formData.raceClass === 'pro' ? '*' : '(neobligāts)'}
              </span>
            </label>
            <input type="text" id="licence" name="licence" value={formData.licence} onChange={handleChange} placeholder="FMN/FIM licences numurs" className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.licence ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.licence} aria-describedby={errors.licence ? 'licence-error' : undefined} data-id="element-639" />
            {errors.licence && <p id="licence-error" className="mt-1.5 text-xs text-red-400" data-id="element-640">
                {errors.licence}
              </p>}
            {formData.raceClass === 'pro' && <p className="mt-1.5 text-xs text-orange-400" data-id="element-641"></p>}
          </div>

          {/* Submit */}
          <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 disabled:cursor-not-allowed text-black font-bold text-lg uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-3" data-id="element-642">
            {isSubmitting ? <>
                <svg className="animate-spin h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true" data-id="element-643">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-644" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-645" />
                </svg>
                Apstrādā reģistrāciju...
              </> : <>
                <SendIcon className="h-5 w-5" data-id="element-646" />
                Pabeigt reģistrāciju
              </>}
          </button>

          <p className="mt-4 text-center text-xs text-gray-500" data-id="element-647">
            Reģistrējoties, jūs piekrītat sacensību noteikumiem, drošības
            regulām un uzņematies visus riskus, kas saistīti ar hard enduro
            sacensībām.
          </p>
        </form>
      </div>
    </section>;
}